/**
 * The previous position a quantity proposal row carries — what the portfolio held before the
 * proposal — read off the backend's composition row or stamped on a row the editor adds. The
 * proposal columns (`./proposal-columns`) read it; this module stays free of grid code so the
 * repository and the builder can import it.
 */
import type { ReviewTicker } from "@eu.mdotm.api/sphere";

/**
 * The instrument's position in the current composition, carried on a proposal row. Absent where the
 * backend sends none — a weights portfolio, cash in a currency with no stored value — and the cells
 * then read N/A, never 0.
 */
export type PreviousPosition = {
	previousQuantity?: number;
	previousMarketValue?: number;
};

/** The previous position a composition row carries, ready to spread onto the grid row. */
export function previousPosition(ticker: ReviewTicker): PreviousPosition {
	const { previousQuantity, previousMarketValue } = ticker;
	return { previousQuantity, previousMarketValue };
}

/**
 * Stamps a row the user adds in the editor as not held before, the way the backend marks an
 * instrument the optimizer adds: its deltas then read the whole new position. A row held by value
 * holds no shares, so it gets no previous quantity.
 */
export function asNewPosition<T extends { heldByValue?: boolean }>(row: T): T & PreviousPosition {
	return { ...row, previousQuantity: row.heldByValue ? undefined : 0, previousMarketValue: 0 };
}
